<template>
  <div />
</template>

<script>
export default {
  setup() {
    const now = useNow();

    // Computed
    const newWebsocketRequests = computed(() => useNuxtApp().$store.state.operator.requests.newWebsocketRequests);

    return {
      now,
      newWebsocketRequests,
    };
  },

  data() {
    return {
      isLoading: false,
      emergencyNotifys: [],
      closedEmergencyRequestIds: [],
      audio: {
        newRequest: new Audio('/sounds/request_new.mp3'),
        emergency: new Audio('/sounds/request_new_emergency.mp3'),
        planned: new Audio('/sounds/request_plan.mp3'),
        planned2x: new Audio('/sounds/request_plan_2x.mp3'),
      },
      timeTickPrevious: null,
    };
  },

  computed: {
    lastNewWebsocketRequest() {
      return this.newWebsocketRequests
        ? this.newWebsocketRequests[this.newWebsocketRequests.length - 1]
        : undefined;
    },
    requestsEmergency() {
      const user = useAuthStore().user;
      if (!user || user.service_id !== 1) { return []; }
      return this.$store.state.operator.requests.requests.filter((request) => request.type_id === this.$store.state.requests.EMERGENCY_TYPE_ID && request.state.id === 1 && !this.closedEmergencyRequestIds.includes(request.id));
    },
    unassignedRequests() {
      return this.$store.getters['operator/requests/getDashboardRequests'].filter((request) => !request.operator);
    },
    isRequestSoundAllowed() {
      // The video pop-out (openVideoWindow in the chat page) is a second window on
      // the same request with its own copy of the store, so it never sees the switch
      // flipped in the main window. It therefore stays out of ordinary request sounds
      // altogether: the window it was opened from is still there and plays for both.
      // The SOS alarm does not come through here and still sounds in both windows.
      return useRequestSoundsStore().isPlaybackAllowed && this.$route.hash !== '#windowed';
    },
  },

  watch: {
    async newWebsocketRequests() {
      if (!this.newWebsocketRequests.length) { return }
      const lastReq = this.lastNewWebsocketRequest;

      if (matchesOperatorRequestsFilter(lastReq, readOperatorRequestsFilter())) {
        await this.playNewRequestSound(lastReq.id);
      }
    },

    now: {
      handler(now) {
        // on time tick check all requests and throw a message about a planned request 15 and 5 minutes ahead of time
        const nowMoment = dayjs(now).startOf('minute');

        this.unassignedRequests.filter((req) => req.has_book_time).forEach((req, index) => {
          const bookMoment = dayjs(req.book_time);

          const diff = bookMoment.diff(nowMoment, 'minute');

          if (!this.timeTickPrevious || !nowMoment.isSame(this.timeTickPrevious, 'minutes')) {
            if (diff === 5 || diff === 0) {
              setTimeout(async() => {
                ElMessage.warning({
                  message: `Naplánovaný <a href="/operator/requests/${req.id}/detail">požadavek</a> za ${diff} minut.`,
                  dangerouslyUseHTMLString: true,
                  duration: 4000,
                  showClose: true,
                });

                await this.playRequestSound(diff === 5 ? 'planned' : 'planned2x');
              }, 10 * index);
            }
          }
        });

        this.timeTickPrevious = nowMoment;
      },
    },

    requestsEmergency: {
      deep: true,
      handler() {
        this.checkEmergency();
      },
    },

    emergencyNotifys: {
      async handler(emergencyNotifys) {
        if (emergencyNotifys && emergencyNotifys.length) {
          const isPlayedByExtension = await useUIStore().chromeExtensionSendMessage({ playAudio: 'emergency' });
          if (!isPlayedByExtension) {
            this.audio.emergency.loop = true;
            this.audio.emergency.currentTime = 0;
            this.audio.emergency.play().catch((error) => {
              console.warn(error);
              setTimeout(() => {
                this.checkEmergency();
              }, 2 * 1000);
            });
          }
        } else {
          const isPlayedByExtension = await useUIStore().chromeExtensionSendMessage({ stopAudio: 'emergency' });
          if (!this.audio.emergency.paused && !isPlayedByExtension) {
            this.audio.emergency.pause();
          }
        }
      },
    },
  },

  mounted() {
    this.audio.emergency.addEventListener('play', (event) => console.log('▶️ AUDIO: Emergency', event));
    this.audio.newRequest.addEventListener('play', (event) => console.log('▶️ AUDIO: newRequest', event));
    this.audio.planned.addEventListener('play', (event) => console.log('▶️ AUDIO: planned', event));
    this.audio.planned2x.addEventListener('play', (event) => console.log('▶️ AUDIO: planned2x', event));
  },

  methods: {
    // Ordinary request sounds only, muted per interpreter. The SOS emergency sound
    // deliberately bypasses this helper so it can never be silenced by the setting.
    async playRequestSound(key) {
      if (!this.isRequestSoundAllowed) { return }

      const isPlayedByExtension = await useUIStore().chromeExtensionSendMessage({ playAudio: key });
      if (isPlayedByExtension) { return }

      this.audio[key].currentTime = 0;
      this.audio[key].play().catch((error) => {
        console.warn(error);
      });
    },

    async playNewRequestSound(requestId) {
      // The sound and the notification are claimed separately. The mute belongs to
      // one request and is therefore set in the tab holding that request, not in
      // the operator's other tabs, so a muted tab has to stay out of the sound
      // claim — winning it would leave the sound unplayed everywhere — while it
      // still takes part in the notification claim, which no switch silences.
      // The notification is claimed first so that every tab reaches that claim at
      // the same point: behind the sound claim, a muted tab would get there an await
      // earlier than the tabs that play, and would win the notification every time.
      if (useAuthStore().user?.lang === 'ua' && await this.claimNewRequest(requestId, 'notification')) {
        useNotificationApi().send('Tichá linka', {
          body: 'Nový požadavek!',
        });
      }

      if (this.isRequestSoundAllowed && await this.claimNewRequest(requestId, 'sound')) {
        await this.playRequestSound('newRequest');
      }
    },

    claimNewRequest(requestId, channel) {
      // The backend sends new.request once per request, but Channels fans it out
      // to every connection in the online_operators group — so each open operator
      // tab receives it. Returns true if this tab is the one that should act on
      // requestId for that channel. The lock is arbitrated by the browser process,
      // so exactly one tab can hold it: that tab acts and the others find it taken
      // and stay quiet. The tabs receive the broadcast a few milliseconds apart, so
      // the holder keeps the lock well beyond that skew before releasing it.
      // (A shared localStorage flag can't do this — each tab caches localStorage
      // per renderer process, so a value just written by one tab is not reliably
      // visible to another within that few-millisecond race.)
      // Web Locks need a secure context: over HTTPS and localhost this holds, but
      // on a plain-HTTP LAN URL navigator.locks is undefined and each tab acts.
      if (requestId == null || typeof navigator === 'undefined' || !navigator.locks) {
        return true;
      }

      return new Promise((resolve) => {
        navigator.locks
          .request(`operator-new-request-${channel}-${requestId}`, { ifAvailable: true }, (lock) => {
            resolve(Boolean(lock));

            // Hold the lock for a short window so duplicate deliveries skip it.
            return lock ? sleep(30000) : undefined;
          })
          .catch(() => resolve(true));
      });
    },

    checkEmergency() {
      if (!this.requestsEmergency) {
        console.warn('requestsEmergency is not defined');
        return;
      }

      this.requestsEmergency.forEach((request) => {
        const isNotifyExist = this.emergencyNotifys.some(({ id }) => id === request.id);

        if (!isNotifyExist) {
          const notify = ElNotification({
            type: 'error',
            customClass: 'el-notification--danger cursor-pointer',
            title: 'SOS linka',
            message: `${request.id} - ${request.client.first_name} ${request.client.last_name}`,
            duration: 0,
            offset: 80,
            onClick: () => {
              this.$router.push(this.$localePath(`/operator/requests/${request.id}/detail`));
            },
            onClose: () => {
              if (!this.audio.emergency.paused) {
                this.audio.emergency.pause();
              }

              this.closedEmergencyRequestIds.push(request.id);
              this.emergencyNotifys = this.emergencyNotifys.filter(({ id }) => id !== request.id);
            },
          });

          this.emergencyNotifys.push({
            id: request.id,
            notify,
          });
        }
      });

      this.emergencyNotifys = this.emergencyNotifys.filter(({ id, notify }) => {
        const isRequestEmergencyExist = this.requestsEmergency.some((request) => request.id === id);

        if (!isRequestEmergencyExist) {
          notify.close();
        }

        return isRequestEmergencyExist;
      });
    },
  },
};
</script>
